import { useEffect, useMemo, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { mediaUrl } from '../firebase/media';
import { Speaker } from './Speaker';
import type { LessonDoc } from '../engine/types';

marked.setOptions({ gfm: true, breaks: false });

/**
 * §6.4 — lessons are markdown plus static diagrams from Cloud Storage. No video
 * and no third-party embeds (§16.2), so what renders here is exactly what the
 * content pack versioned.
 *
 * The markdown is authored by the vendor and seeded with admin credentials, and
 * security rules make lessons read-only for every client — but it is sanitised
 * anyway, so a compromised authoring path cannot become script execution in a
 * rep's browser.
 */
export function LessonView({ lesson }: { lesson: LessonDoc }) {
  const html = useMemo(
    () => DOMPurify.sanitize(marked.parse(lesson.body) as string),
    [lesson.body]
  );

  return (
    <article className="lesson">
      {/* §16.1 — optional narration, reusing the same pipeline as the calls. */}
      {lesson.audio && (
        <p className="lesson-narration">
          <Speaker src={lesson.audio} label="Listen to this lesson" />
          <span className="small muted">Listen instead</span>
        </p>
      )}
      <div className="lesson-body" dangerouslySetInnerHTML={{ __html: html }} />
      {lesson.diagrams?.map((d) => (
        <Diagram key={d.path} path={d.path} alt={d.alt} />
      ))}
    </article>
  );
}

/**
 * A diagram named by its storage path (§16.2). The path is what the content pack
 * versions; the URL is derived at read time, and a diagram that will not resolve
 * leaves its caption rather than a broken image — the lesson text carries the
 * point on its own.
 */
function Diagram({ path, alt }: { path: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    mediaUrl(path).then(
      (resolved) => { if (!cancelled) setUrl(resolved); },
      (err) => {
        console.error('could not load a lesson diagram', { path, err });
        if (!cancelled) setFailed(true);
      }
    );
    return () => { cancelled = true; };
  }, [path]);

  if (failed) return null;

  return (
    <figure className="lesson-figure">
      {url && <img src={url} alt={alt} loading="lazy" />}
      <figcaption className="small muted">{alt}</figcaption>
    </figure>
  );
}
